// Considered: No existing molecule renders the 2-column cover card (description + applicability)
// specific to the context editor's C-section header. See spec Reuse Audit.
//
// Applicability (column B) is a SELECTABLE SUMMARY only (SC3) — editing happens in the rail via
// `ApplicabilityInspectorMolecule`. Prior to Phase 5 this card hosted the full inline editor
// (SegmentedControl + type MultiSelect + UnifiedFilterEditor), which reflowed the card on every
// edit and exposed perspective/relationship/arc lanes `validateApplicability` can't evaluate
// (SC13) with no lane gating. See spec context-selector-editor.md Phase 5 Reuse Audit.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { InputText } from 'primereact/inputtext';
import type { ContextCoverDef } from '../../../types/contextFlow';
import './ContextCoverCardMolecule.scss';

export interface ContextCoverCardMoleculeProps {
  cover: ContextCoverDef;
  onChange: (cover: ContextCoverDef) => void;
  /** True while the rail's Applicability inspector is open for this context (SC3). */
  applicabilitySelected?: boolean;
  /** Selects/toggles the Applicability rail inspector — editing itself happens there. */
  onSelectApplicability?: () => void;
  readOnly?: boolean;
}

const APPLICABILITY_MODE_ICON: Record<NonNullable<ContextCoverDef['applicabilityMode']>, string> = {
  any: 'pi-globe',
  specific: 'pi-th-large',
  custom: 'pi-filter',
};

/** Count of active conditions in a "custom" applicability filter, for the summary subtitle. */
function customConditionCount(filter: ContextCoverDef['applicabilityFilter']): number {
  if (!filter) return 0;
  return (
    (filter.propertyFilters?.length ?? 0) +
    (filter.perspectiveFilters?.length ?? 0) +
    (filter.relationshipFilters?.length ?? 0) +
    (filter.arcFilters?.length ?? 0) +
    (filter.filterGroups?.length ?? 0)
  );
}

const ContextCoverCardMolecule: React.FC<ContextCoverCardMoleculeProps> = ({
  cover,
  onChange,
  applicabilitySelected = false,
  onSelectApplicability,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [addingUseCase, setAddingUseCase] = useState(false);
  const [newUseCase, setNewUseCase] = useState('');

  const patch = useCallback(
    (partial: Partial<ContextCoverDef>) => onChange({ ...cover, ...partial }),
    [cover, onChange]
  );

  const handleAddUseCase = useCallback(() => {
    if (newUseCase.trim()) {
      const useCaseTags = [...(cover.useCaseTags ?? []), newUseCase.trim()];
      patch({ useCaseTags });
      setNewUseCase('');
      setAddingUseCase(false);
    }
  }, [newUseCase, cover, patch]);

  const mode = cover.applicabilityMode ?? 'any';

  const modeLabel = useMemo(() => {
    if (mode === 'specific') return t('context.editor.applicabilitySpecificTypes', 'Specific Sphere types only');
    if (mode === 'custom') return t('context.editor.applicabilityCustomFilter', 'Custom filter');
    return t('context.editor.applicabilityAllTypes', 'All sphere types');
  }, [mode, t]);

  const summaryText = useMemo(() => {
    if (mode === 'specific') {
      return t('context.editor.cover.applicabilitySummaryTypes', { count: cover.applicabilityTypes?.length ?? 0 });
    }
    if (mode === 'custom') {
      return t('context.editor.cover.applicabilitySummaryCustom', { count: customConditionCount(cover.applicabilityFilter) });
    }
    return t('context.editor.cover.applicabilitySummaryAny');
  }, [mode, cover.applicabilityTypes, cover.applicabilityFilter, t]);

  return (
    <div className="context-cover-card-molecule">
      {/* Cover column A */}
      <div className="context-cover-card-molecule__col">
        <div className="context-cover-card-molecule__section-header">
          <span className="context-cover-card-molecule__badge" aria-hidden="true">A</span>
          <div>
            <div className="context-cover-card-molecule__section-title">
              {t('context.editor.cover.title', 'Cover')}
            </div>
            <div className="context-cover-card-molecule__section-sub">
              {t('context.editor.cover.subtitle', 'Name, description, and use case tags. Shown wherever this context appears.')}
            </div>
          </div>
        </div>

        <div className="context-cover-card-molecule__field">
          <div className="context-cover-card-molecule__field-label">
            {t('context.editor.description')}
          </div>
          <textarea
            className="context-cover-card-molecule__textarea"
            value={cover.description}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder={t('context.editor.descriptionPlaceholder')}
            disabled={readOnly}
            rows={3}
          />
        </div>

        <div className="context-cover-card-molecule__field">
          <div className="context-cover-card-molecule__field-label">
            {t('context.editor.useCaseTags', 'Use cases')}
          </div>
          <div className="context-cover-card-molecule__tags-container">
            <div className="context-cover-card-molecule__tags">
              {(cover.useCaseTags ?? []).map((tag) => (
                <span key={tag} className="context-cover-card-molecule__tag">
                  {tag}
                  {!readOnly && (
                    <button
                      type="button"
                      className="context-cover-card-molecule__tag-remove"
                      aria-label={t('context.editor.cover.removeTag', { tag })}
                      onClick={() =>
                        patch({ useCaseTags: (cover.useCaseTags ?? []).filter((u) => u !== tag) })
                      }
                    >
                      <i className="pi pi-times" aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {!readOnly && !addingUseCase && (
              <button
                type="button"
                className="context-cover-card-molecule__add-tag"
                onClick={() => setAddingUseCase(true)}
              >
                <i className="pi pi-plus" /> {t('context.editor.cover.addTag', 'Add tag')}
              </button>
            )}
          </div>
          {addingUseCase && !readOnly && (
            <div className="context-cover-card-molecule__add-tag-input">
              <InputText
                value={newUseCase}
                onChange={(e) => setNewUseCase(e.target.value)}
                placeholder={t('context.editor.cover.enterTag', 'Enter tag')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddUseCase();
                  if (e.key === 'Escape') {
                    setAddingUseCase(false);
                    setNewUseCase('');
                  }
                }}
                autoFocus
              />
            </div>
          )}
        </div>
      </div>

      {/* Applicability column B */}
      <div className="context-cover-card-molecule__col">
        <div className="context-cover-card-molecule__section-header">
          <span className="context-cover-card-molecule__badge" aria-hidden="true">B</span>
          <div>
            <div className="context-cover-card-molecule__section-title">
              {t('context.editor.applicabilityTitle', 'Applicability')}
            </div>
            <div className="context-cover-card-molecule__section-sub">
              {t('context.editor.applicabilityDescription', 'Choose which sphere types this context applies to. Leave as "All types" to make it available everywhere.')}
            </div>
          </div>
        </div>

        {/* Selectable summary only — editing happens in the rail (SC3). */}
        <button
          type="button"
          className={`context-cover-card-molecule__applicability-summary${
            applicabilitySelected ? ' context-cover-card-molecule__applicability-summary--selected' : ''
          }`}
          onClick={() => onSelectApplicability?.()}
          data-testid="context-cover-applicability-summary"
        >
          <span className="context-cover-card-molecule__applicability-summary-icon" aria-hidden="true">
            <i className={`pi ${APPLICABILITY_MODE_ICON[mode]}`} />
          </span>
          <span className="context-cover-card-molecule__applicability-summary-text">
            <span className="context-cover-card-molecule__applicability-summary-title">{modeLabel}</span>
            <span className="context-cover-card-molecule__applicability-summary-sub">{summaryText}</span>
          </span>
          {!readOnly && (
            <span className="context-cover-card-molecule__applicability-summary-edit">
              {t('context.editor.cover.applicabilityEditCta')}
              <i className="pi pi-chevron-right" aria-hidden="true" />
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default ContextCoverCardMolecule;
